import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useConfigLoader } from '@/contexts/ConfigLoaderContext';

interface SplashScreenProps {
  children: React.ReactNode;
}

/**
 * SplashScreen Component
 *
 * Unified loading screen that orchestrates ALL configuration loading:
 * - Zustand store hydration
 * - System settings from backend
 * - Theme application
 * - RTL and typography setup
 *
 * Prevents FOUC by ensuring UI only renders when everything is ready
 */
export function SplashScreen({ children }: SplashScreenProps) {
  const { stage, isReady, stageMessage } = useConfigLoader();
  const [progress, setProgress] = useState(0);

  // Animate progress based on loading stage
  useEffect(() => {
    const stageProgress = {
      'initializing': 0,
      'checking-connection': 20,
      'hydrating-store': 40,
      'loading-settings': 60,
      'applying-theme': 80,
      'ready': 100,
      'error': 100,
    };

    const targetProgress = stageProgress[stage];

    // Smooth progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= targetProgress) {
          clearInterval(progressInterval);
          return targetProgress;
        }
        return prev + 2; // Increment by 2% each frame
      });
    }, 50); // Update every 50ms

    return () => clearInterval(progressInterval);
  }, [stage]);

  // Show splash screen while loading
  if (!isReady) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
        >
          {/* Logo/Brand Area */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="text-center">
              <h1 className="text-5xl font-bold text-white mb-2 tracking-tight">
                Chamama
              </h1>
              <p className="text-gray-400 text-lg">Booking Center</p>
            </div>
          </motion.div>

          {/* Animated Spinner */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }}
            className="mb-8"
          >
            <Loader2 className="w-16 h-16 text-blue-500" />
          </motion.div>

          {/* Progress Bar */}
          <div className="w-64 mb-6">
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                initial={{ width: '0%' }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Status Text - Shows current stage */}
          <motion.p
            key={stage} // Re-animate on stage change
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-gray-400 text-sm"
          >
            {stageMessage}
          </motion.p>

          {/* Loading Dots Animation */}
          <div className="flex gap-2 mt-4">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-blue-500 rounded-full"
                animate={{
                  opacity: [0.3, 1, 0.3],
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2
                }}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Configuration complete - show app
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {children}
    </motion.div>
  );
}
